const en = {
  common: {
    save: `Save`,
    saving: `Saving…`,
    cancel: `Cancel`,
    loading: `Loading…`,
    back: `← Back`,
    optional: `(optional)`,
    getStarted: `Get started`,
    signIn: `Sign in`,
    signOut: `Sign out`,
    learnMore: `Learn more`,
    or: `or`,
    yes: `Yes`,
    no: `No`,
    close: `Close`,
    delete: `Delete`,
    edit: `Edit`,
    submit: `Submit`,
    search: `Search`,
    browseVellum: `Browse Vellum`,
    goBack: `Go back`,
    upgradeLabel: `upgrade to Pro`,
    becomeCreator: `become a creator`,
    verified: `Verified`,
    notVerified: `Not verified`,
  },

  nav: {
    features: `Features`,
    readers: `Readers`,
    pricing: `Pricing`,
    referrals: `Referrals`,
    signIn: `Sign in`,
    startWriting: `Start writing`,
    proLabel: `Pro`,
  },

  avatarUploader: {
    upload: `Upload photo`,
    change: `Change photo`,
    uploading: `Uploading…`,
    success: `Profile photo updated.`,
    hint: `JPG, PNG, WEBP, or GIF. Up to 5 MB.`,
    errors: {
      notImage: `Please choose an image file.`,
      tooLarge: `That file is over 5 MB. Try a smaller one.`,
      uploadFailed: `Upload failed. Please try again.`,
    },
  },

  landing: {
    meta: {
      description: `Vellum — tier-gated long-form writing for creators. Publish, monetise, and connect.`,
    },
    hero: {
      eyebrow: `The writing platform without limits`,
      headline1: `Write freely.`,
      headline2: `Earn boldly.`,
      sub: `Vellum is the first long-form writing platform with genuine multi-tier paywalls, no content restrictions, and tools built for serious creators.`,
      cta: "Start writing — it's free",
      secondaryCta: `See how it works`,
      writersCount: `+{{count}} writers`,
    },
    heroCard: {
      title: `The Architecture of Solitude`,
      author: `Eleanor Park`,
      meta: `8 min read · Supporter tier`,
      badge: `Premium`,
      floatLabel: `This month`,
      floatValue: `$4,280`,
      floatTrend: `23%`,
      newSubscriber: `New subscriber`,
    },
    stats: {
      writers: `Active writers`,
      journals: `Journals published`,
      subscriptions: `Reader subscriptions`,
      paidOut: `Paid out to creators`,
    },
    features: {
      eyebrow: `For creators`,
      title: `The tools serious\nwriters actually need`,
      sub: `Every feature designed around one goal: turn your words into a sustainable income.`,
      list: {
        tiers: {
          title: `Multi-tier paywalls`,
          body: `Create up to 5 subscription tiers at different price points. Gate individual journals behind any tier — not just free/paid.`,
        },
        email: {
          title: `Email newsletter delivery`,
          body: `Pro creators push every publish to subscriber inboxes. Open and click tracking included — no third-party email tool required.`,
        },
        arc: {
          title: `ARC distribution`,
          body: `Send watermarked advance review copies to beta readers before launch. Collect star ratings and written feedback, all in one place.`,
        },
        noRestrictions: {
          title: `No content restrictions`,
          body: 'Publish fiction, essays, personal writing — anything legal. No shadow-banning, no arbitrary policy strikes.',
        },
        protection: {
          title: `Content protection`,
          body: `Visible watermarks and invisible zero-width encoding tie every subscriber view to a specific reader — pirated copies can be traced.`,
        },
        editor: {
          title: `Rich text editor`,
          body: `TipTap-powered editor with tables, embeds, code blocks, and images. Writes like Notion, publishes like a magazine.`,
        },
        versionHistory: {
          title: `Version history`,
          body: `Every save creates a revision. Roll back to any point in your writing history. Free shows the last 20 saves; Pro unlocks the full archive.`,
        },
        scheduled: {
          title: `Scheduled publishing`,
          body: "Write now, publish later. Schedule journals to go live automatically — even if you're offline.",
        },
        series: {
          title: `Journal series`,
          body: `Group related journals into ordered series. Readers can follow a narrative arc across multiple entries.`,
        },
      },
    },
    spotlight: {
      eyebrow: `Spotlight`,
      title: `The only platform with real multi-tier paywalls`,
      body: `Other platforms give you one paywall. Vellum gives you five. Charge $5/mo for casual readers, $15/mo for your inner circle, and $30/mo for superfans who want everything.`,
      perks: {
        perJournal: `Per-journal tier requirements`,
        autoVerify: `Automatic access verification`,
        stripe: `Stripe-powered recurring billing`,
        cancel: `Cancel anytime, no lock-in`,
      },
      cta: `Set up your tiers`,
    },
    tiers: {
      reader: { name: `Reader`, perks: [`All public journals`] },
      supporter: {
        name: `Supporter`,
        perks: [`All public`, `Supporter exclusives`, `Early access`],
      },
      innerCircle: {
        name: `Inner Circle`,
        perks: [`Everything`, `Direct messages`, `Drafts & process`],
      },
    },
    forReaders: {
      eyebrow: `For readers`,
      title: `The writing you love,`,
      titleEm: `without the noise`,
      sub: "Discover writers who publish without fear. Read long-form work that doesn't fit anywhere else.",
      cta: `Start reading free`,
      screenshotLabel: `Reader experience screenshot`,
      currentlyReading: `Currently reading`,
      bookmarked: `{{count}} journals bookmarked`,
      features: {
        discovery: {
          title: `Curated discovery`,
          body: `Browse by tag, trending, or follow your favourite writers for a personalised feed.`,
        },
        bookmark: {
          title: `Bookmark & collect`,
          body: `Save journals to revisit later. Vellum picks up where you left off.`,
        },
        support: {
          title: `Support directly`,
          body: `Every subscription goes to the writer. No middleman taking a cut of their art.`,
        },
        realtime: {
          title: `Real-time updates`,
          body: "New journals appear in your feed the moment they're published — no refresh needed.",
        },
      },
    },
    testimonials: {
      eyebrow: `Voices`,
      title: `Writers who chose freedom`,
      list: [
        {
          quote:
            "I moved here from another platform after they removed three years of my work with no warning. On Vellum I've rebuilt my audience and doubled my income in six months.",
          name: `Marcela Voss`,
          role: `Fiction writer · 1,800 subscribers`,
        },
        {
          quote: `The multi-tier system changed everything. My casual readers pay $8, my superfans pay $30. I earn five times what I did when everyone was on the same plan.`,
          name: `Daniel Huang`,
          role: `Essays & criticism · 940 subscribers`,
        },
        {
          quote: `I was scared to publish certain topics anywhere else. Vellum doesn't police my subject matter. My readers found me here and they're the best community I've ever had.`,
          name: `Sasha Reid`,
          role: `Personal essays · 2,200 subscribers`,
        },
      ],
    },
    howItWorks: {
      eyebrow: `Process`,
      title: `Live in three steps`,
      steps: [
        {
          n: `01`,
          title: `Create your account`,
          body: `Sign up in under a minute. No credit card required. Your first journal can be live today.`,
        },
        {
          n: `02`,
          title: `Set up your tiers`,
          body: `Decide what you want to charge. Name your tiers, set prices, describe the perks. Takes five minutes.`,
        },
        {
          n: `03`,
          title: `Publish and earn`,
          body: `Write in our editor, hit publish, and watch subscriptions arrive. Payouts via Stripe, monthly.`,
        },
      ],
    },
    pricing: {
      eyebrow: `Pricing`,
      title: `Simple, creator-first pricing`,
      sub: `Vellum takes a small platform fee on subscription revenue. You keep the rest.`,
      note: `Vellum charges a 6% platform fee on creator revenue — dropping to 5.5% on Pro and 5% on Author. Stripe processing fees apply. No monthly charge to publish.`,
      mostPopular: `Most popular`,
      tierReader: {
        name: `Reader`,
        price: `Free`,
        perks: [
          `Browse public journals`,
          `Bookmark & save`,
          `Follow writers`,
          `Real-time feed`,
          `Search & discovery`,
        ],
      },
      tierSupporter: {
        name: `Supporter`,
        priceNote: `set by creator`,
        perks: [
          `Everything in Reader`,
          `Access to gated journals`,
          `Direct creator support`,
          `Early access to drafts`,
          `Community access`,
        ],
      },
      tierCreator: {
        name: `Creator`,
        price: `Free to start`,
        perks: [
          `Unlimited journals`,
          `Up to 5 subscription tiers`,
          `Version history (last 20 saves)`,
          `Analytics dashboard`,
          `Stripe payouts`,
          `Series & collections`,
        ],
      },
    },
    comparison: {
      eyebrow: `Why Vellum`,
      title: `Built for writers\nother platforms won't touch`,
      others: `Others`,
      us: `Vellum`,
      features: [
        `Multi-tier paywalls`,
        `No content restrictions`,
        `Email newsletter delivery`,
        `ARC distribution for beta readers`,
        `Content watermarking & protection`,
        `Version history`,
        `Real-time reader feed`,
        `Journal series`,
        `Paywall per journal`,
        `Long-form editor`,
        `Creator payouts`,
      ],
    },
    cta: {
      eyebrow: `Ready?`,
      title: `Your writing deserves`,
      titleEm: `a better home.`,
      sub: `Join {{count}}+ writers who publish without compromise on Vellum. Free to start.`,
      primary: `Create your account`,
      secondary: `Sign in`,
    },
    aiTransparency: {
      eyebrow: `Our approach to AI`,
      title: `Built for writers, not instead of them`,
      sub: `Vellum's AI writing tools help you draft, refine, and publish — on your terms. When you use our AI, your journals are automatically labeled so readers always know how a piece was made. We think transparency is good for writers and good for the craft.`,
      tiers: {
        assisted: {
          label: `AI-assisted`,
          desc: `AI gave feedback or outlined chapters. You wrote every word.`,
        },
        collaborated: {
          label: `AI-collaborated`,
          desc: `AI helped shape your blurbs or summaries. The story is yours.`,
        },
        generated: {
          label: `AI-generated`,
          desc: `AI wrote the prose using our story generator. Disclosed clearly.`,
        },
      },
      learnMore: `Learn about AI disclosure →`,
    },
    footer: {
      tagline: `Long-form writing, honestly monetised.`,
      complianceAria: `This site is compliant with GDPR and CCPA regulations.`,
      platform: `Platform`,
      account: `Account`,
      support: `Support`,
      links: {
        features: `Features`,
        pricing: `Pricing`,
        forReaders: `For readers`,
        discover: `Discover`,
        pro: `Vellum Pro`,
        referral: `Referral program`,
        getStarted: `Get started`,
        signIn: `Sign in`,
        settings: `Settings`,
        dashboard: `Dashboard`,
      },
      copyright: `© {{year}} Vellum. All rights reserved.`,
      privacy: `Privacy`,
      terms: `Terms`,
      cookies: `Cookie Policy`,
      dmca: `DMCA`,
      refunds: `Refunds`,
      community: `Community Guidelines`,
      csam: `CSAM Policy`,
      cookiePrefs: `Cookie preferences`,
    },
  },

  auth: {
    login: {
      meta: { title: `Sign in`, description: `Sign in to your Vellum account.` },
      title: `Welcome back`,
      subtitle: `Sign in to your account`,
      email: `Email`,
      password: `Password`,
      forgotPassword: `Forgot password?`,
      submit: `Sign in`,
      submitting: `Signing in…`,
      noAccount: "Don't have an account?",
      register: `Get started`,
    },
    register: {
      meta: {
        title: `Create an account`,
        description: `Create your free Vellum account and start publishing.`,
      },
      title: `Start writing`,
      subtitle: `Create your free account`,
      email: `Email`,
      username: `Username`,
      displayName: `Display name`,
      namePlaceholder: `Your Name`,
      password: `Password`,
      passwordHint: `At least 8 characters`,
      usernameHint: `Letters, numbers, and underscores only`,
      submit: `Create account`,
      submitting: `Creating account…`,
      hasAccount: `Already have an account?`,
      signIn: `Sign in`,
      termsAcknowledgment: `By creating an account you agree to our Terms of Service, Privacy Policy, and Community Guidelines.`,
      termsLink: `Terms`,
      privacyLink: `Privacy`,
      communityLink: `Community Guidelines`,
      comingSoon: {
        title: `Coming soon`,
        body: `Vellum is not open to the public yet. Check back soon.`,
      },
      errors: {
        registrationFailed: `Registration failed`,
      },
    },
    forgotPassword: {
      title: `Reset password`,
      subtitle: `Enter your email to get a reset link`,
      email: `Email`,
      submit: `Send reset link`,
    },
    errors: {
      loginFailed: `Login failed`,
      unknownError: `Unknown error`,
    },
  },

  notFound: {
    meta: {
      title: `Page not found`,
      description: `The page you were looking for could not be found.`,
    },
    code: `404`,
    title: `Page not found`,
    body: `The page you're looking for doesn't exist, or may have been moved or made private by its author.`,
    goBack: `Go back`,
    browse: `Browse Vellum`,
  },

  settings: {
    meta: { title: `Settings`, description: `Manage your Vellum account settings.` },
    title: `Settings`,
    tabs: {
      account: `Account`,
      preferences: `Preferences`,
      security: `Security`,
      profile: `Profile`,
      billing: `Billing`,
      referrals: `Referrals`,
      creatorCode: `Creator Code`,
    },
    sections: {
      appearance: `Appearance`,
      reading: `Reading`,
      tts: `Text-to-speech`,
      profile: `Profile`,
      password: `Password`,
      accountDetails: `Account details`,
      twoFactor: `Two-factor authentication`,
      linkedAccounts: `Linked accounts`,
      customDomain: `Custom domain`,
      profileTheming: `Profile theming`,
      vellumPro: `Vellum Pro`,
      creatorAccount: `Creator account`,
      creatorPayouts: `Creator payouts`,
      subscriptionManagement: `Subscription management`,
      dangerZone: `Danger zone`,
      contentPrivacy: `Content & Privacy`,
    },
    dangerZone: {
      delete: {
        title: `Delete account`,
        body: `Deleting your account signs you out everywhere, anonymises your profile (posts appear as "[deleted account]"), cancels your active subscriptions at the end of the current billing period, and removes your linked sign-in providers. Payment and tax records are retained as required by law. This action cannot be undone — to restore access you would need to register a new account.`,
        cta: `Delete account`,
        modalTitle: `Delete your account?`,
        confirmLabel: `Delete my account`,
        modalBody: `This cannot be undone. Active subscriptions will be cancelled at period end. Enter your username to confirm.`,
        typeUsername: `Type your username`,
        password: `Current password`,
        usernameMismatch: `Username does not match.`,
        success: `Your account has been deleted.`,
        error: `Could not delete your account. Please contact support.`,
      },
      erasure: {
        title: `Request data erasure`,
        body: `Exercise your right to erasure under GDPR Article 17 or your right to delete under CCPA § 1798.105. Unlike account deletion, this also purges data we are not legally required to retain. We will verify your identity and respond within 30 days (GDPR) or 45 days (CCPA).`,
        cta: `Request data erasure`,
        modalTitle: `Request full data erasure`,
        confirmLabel: `Submit request`,
        modalBody: `This submits a formal request to our privacy team. It does not immediately delete your account — we will verify your identity and respond with the scope of what was deleted and any data we are legally required to retain.`,
        reason: `Legal basis`,
        reasonOptions: {
          gdpr: `GDPR Article 17 (right to erasure)`,
          ccpa: `CCPA § 1798.105 (right to delete)`,
          other: `Other`,
        },
        notes: `Notes (optional)`,
        notesPlaceholder: `Anything you want our privacy team to know about this request.`,
        success: `Request submitted. We will respond within 30 days (GDPR) or 45 days (CCPA).`,
        error: `Could not submit your request. Please try again or email privacy@writevellum.com.`,
      },
    },
    appearance: {
      light: `Light`,
      dark: `Dark`,
      system: `System`,
    },
    reading: {
      serif: `Serif`,
      sansSerif: `Sans-serif`,
      accessibility: `Accessibility`,
    },
    ttsSection: {
      proGateTitle: `Text-to-speech is a Vellum Pro feature`,
      proGateBody: `Have any journal read aloud in a natural, human-like voice — all processed privately on your device. Upgrade to Pro to unlock it.`,
      privacyNote: `Fully private. All speech is generated on your device using a local AI model — your journal content is never sent to any server.`,
      voiceAria: `TTS voice`,
    },
    profile: {
      avatar: `Profile photo`,
      avatarDisabledHint: `Verify your email to upload a profile photo.`,
      displayName: `Display name`,
      bio: `Bio`,
      bioPlaceholder: `A few words about yourself`,
      website: `Website`,
      websitePlaceholder: `https://yoursite.com`,
      saveChanges: `Save changes`,
      saving: `Saving…`,
    },
    password: {
      current: `Current password`,
      new: `New password`,
      confirm: `Confirm new password`,
      change: `Change password`,
      changing: `Changing…`,
      errors: {
        noMatch: `Passwords do not match`,
        tooShort: `Password must be at least 8 characters`,
      },
      success: `Password changed. You may need to sign in again.`,
    },
    accountDetails: {
      username: `Username`,
      email: `Email`,
      accountType: `Account type`,
      emailVerified: `Email verified`,
      newUsername: `New username`,
      newEmail: `New email`,
      updateAccount: `Update account`,
      updating: `Saving…`,
      proLabel: `Vellum Pro`,
      proCreator: `Vellum Pro · Creator`,
      authorLabel: `Vellum Author`,
      authorCreator: `Vellum Author · Creator`,
      creatorLabel: `Creator`,
      readerLabel: `Reader`,
      errors: {
        enterEmailOrUsername: `Enter a new email or username to update`,
        invalidUsername: `Username must be 3–32 alphanumeric characters or underscores`,
      },
      success: {
        emailUpdated: `Email updated — check your inbox to verify the new address`,
        usernameUpdated: `Username updated`,
      },
    },
    domain: {
      title: `Custom domain`,
      proGateTitle: `Custom domain is a Vellum Author feature`,
      proGateBody: `Publish at yourname.com instead of writevellum.com/yourname. Upgrade to Author to unlock it.`,
      step1: `Step 1 — Point your domain at Vellum`,
      step1Body: `In your DNS provider, add a CNAME record:`,
      dnsType: `Type`,
      dnsHost: `Name / Host`,
      dnsValue: `Value / Target`,
      dnsTTL: `TTL`,
      dnsHostValue: `@ or www (or your subdomain)`,
      dnsTTLValue: `3600 (or Auto)`,
      step2: `Step 2 — Enter your domain below and save`,
      step3: `Step 3 — Click Verify DNS`,
      step3Body: `(DNS changes can take up to 48 hours to propagate)`,
      placeholder: `yourname.com`,
      save: `Save`,
      saving: `Saving…`,
      verified: `Verified`,
      pending: `◷ Pending verification`,
      verifyDns: `Verify DNS`,
      checkingDns: `Checking DNS…`,
      removeDomain: `Remove domain`,
      removing: `Removing…`,
      liveAt: `Your profile is live at`,
      verifiedAt: `verified`,
      success: {
        saved: `Domain saved. Now verify your DNS record.`,
        verifiedOk: `Domain verified! Your profile is live at {{domain}}`,
        removed: `Custom domain removed.`,
      },
      errors: {
        load: `Failed to load domain settings`,
        save: `Failed to save domain`,
        verify: `Verification check failed`,
        verifyNotFound: `CNAME not found yet — DNS can take up to 48 hours.`,
        remove: `Failed to remove domain`,
      },
    },
    theming: {
      proGateTitle: `Profile theming is a Vellum Pro feature`,
      proGateBody:
        'Custom colors, fonts, and layouts make your profile feel like your brand, not ours. Upgrade to Pro to unlock it.',
      layout: `Layout`,
      colors: `Colors`,
      headingFont: `Heading font`,
      accent: `Accent`,
      background: `Background`,
      text: `Text`,
      saveTheme: `Save theme`,
      saving: `Saving…`,
      layouts: {
        default: `Default`,
        editorial: `Editorial`,
        centered: `Centered`,
        magazine: `Magazine`,
      },
      fonts: {
        default: `Default`,
        systemHeading: `System heading font`,
        searchPlaceholder: `Search fonts…`,
        noMatch: `No fonts match "{{query}}"`,
      },
      success: `Profile theme saved`,
      error: `Failed to save theme`,
    },
    pro: {
      haveProTitle: `You have Vellum Pro`,
      haveProSub: `All Pro features are active on your account.`,
      haveAuthorTitle: `You have Vellum Author`,
      haveAuthorSub: `All Author features are active — ebook export, custom domain, and content protection included.`,
      manageBilling: `Manage billing →`,
      managingBilling: `Opening…`,
      giftPro: `Gift Pro to a friend`,
      upgradeToAuthorTitle: `Unlock Author features`,
      upgradeToAuthorBody: `You're on Pro. Upgrade to Author to add ebook & print export, custom domain, and enhanced content protection.`,
      upgradeToAuthor: `Upgrade to Author →`,
      upgradingToAuthor: `Redirecting…`,
      unlockTitle: `Unlock every Pro feature for $18–24/mo`,
      unlockBody: `Email delivery, ARC distribution, full analytics, unlimited version history, content calendar, manuscript import, co-author collaboration, pay-per-post, gift subscriptions, founding member tiers, and priority support.`,
      monthly: `$24/mo · Monthly`,
      annual: `$18/mo · Annual — save $72`,
      authorMonthly: `$34/mo · Monthly`,
      authorAnnual: `$28/mo · Annual — save $72`,
      upgrade: `Upgrade to Pro →`,
      upgrading: `Redirecting…`,
      seeFeatures: `See all features`,
      memberSince: `Member since`,
      nextCharge: `Next charge`,
      billing: `Billing`,
      managedExternally: `Managed outside Stripe — no renewal date available`,
      cancelPlan: `Cancel Plan`,
      reactivate: `Reactivate`,
      reactivating: `Reactivating…`,
      switchToAnnualAction: `Switch to Annual · Save {{amount}}/yr`,
      proDescription: `Email delivery, analytics, version history, collaboration, monetization tools.`,
      authorBadge: `Vellum Author`,
      authorDescription: `Everything in Pro + ebook export, custom domain, and content protection.`,
      billedYearly: `Billed {{total}}/year`,
      billedMonthly: `Billed monthly`,
      toasts: {
        planCancelled: `Plan cancelled. Access continues until your billing period ends.`,
        reactivated: `Pro subscription reactivated!`,
        reactivateFailed: `Failed to reactivate. Please try again.`,
      },
    },
    creator: {
      badge: `️ Creator`,
      body: `Publish subscriber-only journals and earn directly from your readers. All identity and tax information is handled securely through Stripe — encrypted in transit and at rest.`,
      features: [
        `Create subscription tiers and charge readers monthly`,
        `Gate individual journals to paying subscribers only`,
        `Revenue dashboard with subscriber analytics and CSV export`,
        `Direct bank payouts via Stripe Connect Express`,
      ],
      steps: [
        `Create your creator profile on Vellum`,
        `Verify your identity via Stripe (government-issued ID)`,
        `Submit tax information (W-9 or W-8BEN) — encrypted end-to-end`,
        `Connect a bank account to receive payouts`,
      ],
      activate: `Activate creator account`,
      activating: `Setting up…`,
      connectNotEnrolled: `Stripe Connect is not yet enabled on this Stripe account. Visit dashboard.stripe.com/connect to enroll, then click "Set up payouts" below to continue.`,
      creatorActive: `Your creator account is active — payouts will work once Connect is enrolled.`,
    },
    payouts: {
      body: `Subscriber payments are processed through Stripe Connect. Complete identity verification and tax info to start receiving payouts directly to your bank. All data is encrypted in transit via Stripe.`,
      setup: `Set up payouts →`,
      opening: `Opening…`,
    },
    subscriptions: {
      body: `Manage your active subscriptions, update payment methods, and view billing history through the Stripe customer portal.`,
      openPortal: `Open billing portal →`,
      openingPortal: `Opening portal…`,
      loading: `Loading subscriptions…`,
      none: `You don't have any active creator subscriptions.`,
      tier: `Tier`,
      memberFor: `Member for`,
      sinceSuffix: `· since`,
      nextCharge: `Next charge`,
      yearlyBilledSuffix: `({{total}}/yr billed annually)`,
      switchToAnnual: `Switch to annual`,
      toasts: {
        subCancelled: `Subscription cancelled. Access continues until your billing period ends.`,
      },
    },
    tokens: {
      sectionTitle: `Tokens`,
      balance: `Token balance`,
      balanceBody: `Use tokens to tip creators you love. 1 token = $1 face value.`,
      tokensLabel: `tokens`,
      buy: `Buy tokens`,
      tipDescription: `Tip creators directly from their profile, journal posts, or series pages.`,
    },
    contentPreferences: {
      dob: {
        title: `Date of Birth`,
        body: `Required for age verification. Stored securely and never shared publicly.`,
        label: `Date of birth`,
        save: `Save Date of Birth`,
      },
      ageVerification: {
        title: `Age Verification`,
        status: `Status`,
        body: `Self-declaration unlocks Mature (L1). Payment method verification unlocks NSFW (L2) and Kink (L3). Creators with a Stripe payout account are automatically verified.`,
        upgradePayment: `Upgrade to Payment Verification →`,
        verifyAge: `Verify Age →`,
        verifiedSelfDeclaration: `Verified · Self-declaration`,
        verifiedPaymentMethod: `Verified · Payment Method`,
        verifiedIdDocument: `Verified · ID Document`,
        notVerified: `Not verified`,
      },
      title: `Content Preferences`,
      body: `You must explicitly opt in before you can create or view content at each level.`,
      safeMode: {
        title: `Safe Mode`,
        desc: `Hide all Mature, NSFW, and Kink content regardless of settings below.`,
      },
      mature: {
        title: `Mature Content`,
        desc: `Dark themes, suggestive content. Requires age verification.`,
        warn: `Age verification required to enable.`,
      },
      nsfw: {
        title: `NSFW Content`,
        desc: `Sexually explicit content. Requires payment method verification.`,
        warn: `Payment method verification required.`,
      },
      kink: {
        title: `Kink Content`,
        desc: `Fetish and BDSM content. NSFW must be enabled first.`,
        warn: `Payment method verification required.`,
      },
    },
  },

  languageSwitcher: {
    label: `Language`,
  },

  cookies: {
    banner: {
      ariaLabel: `Cookie consent`,
      title: `We use cookies`,
      body: `We use strictly necessary cookies to keep you signed in. With your consent we'd also like to use preference and analytics cookies to improve the product. You can change your choice any time from the footer.`,
      learnMore: `Learn more`,
      manage: `Manage preferences`,
      reject: `Reject non-essential`,
      accept: `Accept all`,
    },
    modal: {
      title: `Cookie preferences`,
      intro: `Choose which cookies you allow. Strictly-necessary cookies are always on because they're required for sign-in and security. See our`,
      policyLink: `Cookie Policy`,
      introTail: `for the full list.`,
      essential: {
        title: `Strictly necessary`,
        body: `Required for sign-in, CSRF protection, and core platform features. These cookies cannot be turned off.`,
      },
      preferences: {
        title: `Preferences`,
        body: `Remember UI choices like theme, language, and reader font across visits.`,
      },
      analytics: {
        title: `Analytics`,
        body: `Help us understand how the platform is used (PostHog). Anonymous visitors are counted in aggregate; only signed-in users are linked to a profile.`,
      },
      reject: `Reject non-essential`,
      acceptAll: `Accept all`,
      save: `Save preferences`,
    },
  },

  legal: {
    tocHint: `Use ⌘/Ctrl+F to search this page.`,
    docs: {
      terms: {
        title: `Terms of Service`,
        description: `The terms that govern your use of Vellum.`,
      },
      privacy: {
        title: `Privacy Policy`,
        description: `How Vellum collects, uses, and protects your personal data.`,
      },
      communityGuidelines: {
        title: `Community Guidelines`,
        description: `What we expect from everyone who writes and reads on Vellum.`,
      },
      csamPolicy: {
        title: `CSAM Policy`,
        description: `Vellum has zero tolerance for child sexual abuse material. How we prevent and report it.`,
      },
      cookies: {
        title: `Cookie Policy`,
        description: `Which cookies Vellum uses, why, and how you can manage them.`,
      },
      dmca: {
        title: `Copyright & DMCA Policy`,
        description: `How to report copyright infringement on Vellum and how to submit a counter-notification.`,
      },
      refunds: {
        title: `Refund & Cancellation Policy`,
        description: `How to cancel paid products on Vellum and when refunds are available.`,
      },
    },
  },

  pages: {
    forgotPassword: {
      meta: { title: `Reset password`, description: `Reset your Vellum password.` },
      title: `Forgot your password?`,
      subtitle: `Enter your email and we'll send a reset link.`,
      checkEmail: {
        title: `Check your email`,
        body: `If an account exists for {email}, we've sent a reset link. Check your spam folder if you don't see it.`,
        backToSignIn: `← Back to sign in`,
      },
      email: `Email`,
      submit: `Send reset link`,
      submitting: `Sending…`,
      rememberIt: `Remember it?`,
      signIn: `Sign in`,
    },
    resetPassword: {
      meta: { title: `Reset password`, description: `Set a new password for your Vellum account.` },
      invalidLink: {
        title: `Invalid link`,
        subtitle: `This reset link is missing or invalid.`,
        requestNew: `Request new link`,
      },
      title: `Set new password`,
      subtitle: `Choose a strong password for your account.`,
      newPassword: `New password`,
      confirmPassword: `Confirm password`,
      confirmPlaceholder: `Same as above`,
      passwordHint: `At least 8 characters`,
      submit: `Reset password`,
      submitting: `Resetting…`,
      backToSignIn: `← Back to sign in`,
      errors: {
        noMatch: `Passwords do not match`,
        tooShort: `Password must be at least 8 characters`,
        expired: `Reset failed. Your link may have expired.`,
      },
    },
    verifyEmail: {
      meta: { title: `Verify email`, description: `Verify your Vellum email address.` },
      verifying: `Verifying your email…`,
      success: {
        title: `Email verified!`,
        subtitle: `Redirecting you to Vellum…`,
      },
      failed: {
        title: `Verification failed`,
        sendNew: `Send a new link`,
        checkInbox: `Check your inbox`,
        sending: `Sending…`,
        backToSignIn: `Back to sign in`,
      },
      idle: {
        title: `Verify your email`,
        subtitle: `We sent a verification link to your email address. Click the link to activate your account.`,
        resend: `Resend verification email`,
        emailSent: `Email sent`,
        sending: `Sending…`,
        emailSentNote: `Verification email sent — check your inbox and spam folder.`,
        wrongEmail: `Wrong email?`,
        updateInSettings: `Update in settings`,
        signIn: `Sign in`,
      },
    },
    paymentSuccess: {
      proGift: {
        meta: { title: `Gift sent!`, description: `Your Vellum Pro gift is ready.` },
        title: `Gift ready to share!`,
        subtitle: `Your payment was successful. A redemption link for Vellum Pro has been generated. Check your email, or go to Settings → Billing to find and copy the link.`,
        viewGiftLink: `View gift link`,
        goHome: `Go home`,
      },
      pro: {
        meta: { title: `Welcome to Pro!`, description: `You're now on Vellum Pro.` },
        title: `Welcome to Vellum Pro!`,
        subtitle: `All Pro features are now active on your account. Explore ebook export, email delivery, AI writing tools, and more.`,
        dashboard: `Go to dashboard`,
        exploreFeatures: `Explore Pro features`,
      },
      loading: {
        meta: { title: `Payment successful`, description: `Your payment was successful.` },
        purchase: `Confirming your purchase…`,
        subscription: `Confirming your subscription…`,
        moment: `Just a moment while we get everything set up.`,
      },
      error: {
        title: `Something went wrong`,
        subtitle: `We couldn't verify your payment. Please check your email for confirmation or contact support.`,
        goHome: `Go home`,
      },
      purchase: {
        title: `Purchase complete!`,
        subtitle: `You now have permanent access to this post. Start reading.`,
      },
      subscription: {
        title: `You're subscribed!`,
        subtitle: `You now have access to exclusive content. Start reading.`,
      },
      proGiftSubtitleWithLink: `Your payment was successful. A redemption link for Vellum Pro has been generated. Check your email, or go to <1>Settings → Billing</1> to find and copy the link.`,
      tokenPurchase: {
        title: `Tokens added!`,
        subtitle: `Your token balance has been updated. Head to a creator's profile or post to start sending tips.`,
        buyMore: `Buy more tokens`,
      },
      browseJournals: `Browse journals`,
      manageBilling: `Manage billing`,
    },
    giftRedeem: {
      meta: { description: `Claim your gift subscription on Vellum.` },
      loading: { title: `Loading your gift…`, subtitle: `Just a moment.` },
      invalid: {
        title: `Gift unavailable`,
        goToVellum: `Go to Vellum`,
        noToken: `No redemption token found in this link.`,
        alreadyRedeemed: `This gift has already been redeemed by someone else.`,
        expired: `This gift link has expired.`,
        generic: `This gift link is invalid or has expired.`,
      },
      valid: {
        title: `You've received a gift!`,
        subtitle: `{purchaser} gifted you a subscription to {creator}.`,
        creator: `Creator`,
        tier: `Tier`,
        monthlyValue: `Monthly value`,
        fullAccessTo: `You now have full access to`,
        linkExpires: `Link expires`,
        accept: `Accept gift subscription`,
        signInToClaim: `Sign in to claim your gift`,
        activating: `Activating subscription…`,
        noAccount: `Don't have an account?`,
        createFree: `Create one free`,
        moment: `— it only takes a moment.`,
        redeemError: `Redemption failed`,
      },
      success: {
        title: `Subscription activated!`,
        subtitle: `You now have full access to {creator}'s {tier} tier. Start reading.`,
        visitPage: `Visit {creator}'s page →`,
      },
    },
    proGiftRedeem: {
      meta: {
        title: `Claim your Vellum Pro gift`,
        description: `Someone gifted you Vellum Pro access. Claim it here.`,
      },
      loading: { title: `Loading your gift…`, body: `Just a moment.` },
      invalid: {
        title: `Gift unavailable`,
        goToVellum: `Go to Vellum`,
        noToken: `No redemption token found in this link.`,
        alreadyRedeemed: `This gift has already been redeemed.`,
        expired: `This gift link has expired.`,
        generic: `This gift link is invalid or has expired.`,
      },
      valid: {
        title: `You've been gifted Vellum Pro!`,
        subtitle: `{purchaser} gifted you {months} of Vellum Pro — all features, no limits.`,
        allFeatures: `of Vellum Pro — all features, no limits.`,
        continueAfter: `. To continue after that, set up your own subscription in settings.`,
        plan: `Plan`,
        planValue: `Vellum Pro`,
        duration: `Duration`,
        from: `From`,
        linkExpires: `Link expires`,
        accept: `Accept Vellum Pro`,
        signInToClaim: `Sign in to claim your gift`,
        activating: `Activating Pro…`,
        noAccount: `Don't have an account?`,
        createFree: `Create one free`,
        moment: `— it only takes a moment.`,
        month: `month`,
        months: `months`,
        redeemError: `Redemption failed`,
      },
      success: {
        title: `Vellum Pro activated!`,
        body: `You now have access to all Pro features.`,
        expiresOn: `Your Pro access runs until {date}. To continue after that, set up your own subscription in settings.`,
        viewSettings: `View settings →`,
        explore: `Explore Vellum`,
      },
    },
    profile: {
      notFound: `User not found`,
      notFoundMetaTitle: `Profile not found`,
      editProfile: `Edit profile`,
      notableCreator: `Notable creator`,
      journals: `journals`,
      followers: `followers`,
      following: `following`,
      subscribe: `Subscribe`,
      giftSubscription: `Gift subscription`,
      entries: `entries`,
      series: `Series`,
    },
    onboarding: {
      meta: {
        title: `Welcome to Vellum`,
        description: `Pick a plan and set up your profile to start writing on Vellum.`,
      },
      welcome: `Welcome, {{name}}`,
      intro: `Two quick steps and you're set: pick a plan, then make your profile yours.`,
      stepperLabel: `Onboarding progress`,
      steps: {
        plans: `Pick a plan`,
        profile: `Your profile`,
      },
      plans: {
        eyebrow: `Step 1 of 2`,
        title: `Pick the plan that fits`,
        titlePaid: `You're on Vellum {{tier}}`,
        sub: `Free works for everyone. Pro adds the full creator toolkit. Author adds the book-publishing layer on top.`,
        subPaid: `All features are unlocked. You can change or cancel any time from Settings.`,
        billingToggleLabel: `Billing period`,
        monthly: `Monthly`,
        annualWithSave: `Annual · save ~17%`,
        currentPlan: `Your current plan`,
        includedInAuthor: `Included in Author`,
        redirecting: `Redirecting…`,
        continueWithoutUpgrade: `Continue with Free`,
        continueAfterUpgrade: `Continue →`,
        free: {
          name: `Free`,
          price: `$0`,
          billing: `No credit card required`,
          continue: `Start free`,
          perks: [
            `Unlimited journals & series`,
            `Up to 5 subscription tiers`,
            `Basic analytics`,
            `Stripe payouts`,
          ],
        },
        pro: {
          badge: `Most popular`,
          name: `Pro`,
          period: `/mo`,
          billedAnnual: `Billed $216/year`,
          billedMonthly: `Billed monthly · cancel anytime`,
          cta: `Upgrade to Pro`,
          perks: [
            `Everything in Free`,
            `Email delivery to subscribers`,
            `Full engagement analytics`,
            `Profile theming & co-author collaboration`,
            `Pay-per-post & gift subscriptions`,
          ],
        },
        author: {
          badge: `For book publishers`,
          name: `Author`,
          period: `/mo`,
          billedAnnual: `Billed $336/year`,
          billedMonthly: `Billed monthly · cancel anytime`,
          cta: `Upgrade to Author`,
          perks: [
            `Everything in Pro`,
            `Ebook & print export (EPUB, MOBI, PDF)`,
            `Custom domain`,
            `Content protection & watermarking`,
          ],
        },
        errors: {
          checkoutFailed: `We couldn't start checkout. Please try again.`,
        },
      },
      profile: {
        eyebrow: `Step 2 of 2`,
        title: `Make your profile yours`,
        sub: `These are the details readers see when they find you. Everything except your username is optional — you can come back any time from Settings.`,
        avatar: `Profile photo`,
        avatarDisabledHint: `Verify your email to upload a profile photo.`,
        username: `Username`,
        usernameHint: `Letters, numbers, and underscores only. 3–32 characters. This is the URL of your profile page.`,
        displayName: `Display name`,
        displayNamePlaceholder: `Your name`,
        displayNameHint: `Shown on your profile and journals. Defaults to your username if left blank.`,
        bio: `Bio`,
        bioPlaceholder: `A few words about what you write or who you are.`,
        bioHint: `{{remaining}} characters remaining`,
        website: `Website`,
        websitePlaceholder: `https://yoursite.com`,
        verifyNotice: `Verify your email to save these changes.`,
        verifyNoticeResend: `Resend verification email`,
        verifyNoticeSending: `Sending…`,
        verifyNoticeSent: `Verification email sent — check your inbox.`,
        skip: `Skip for now`,
        finishing: `Finishing…`,
        saveAndFinish: `Save and finish`,
        saving: `Saving…`,
        saved: `Profile saved.`,
        errors: {
          usernameRequired: `Choose a username to continue.`,
          usernameInvalid: `Username must be 3–32 letters, numbers, or underscores.`,
          displayNameTooLong: `Display name must be 80 characters or fewer.`,
          bioTooLong: `Bio must be 500 characters or fewer.`,
          saveFailed: `We couldn't save your profile. Please try again.`,
          resendFailed: `We couldn't resend the verification email. Please try again.`,
        },
      },
    },
    search: {
      meta: { title: `Search`, description: `Search for journals, creators, and tags on Vellum.` },
      placeholder: `Search journals and creators…`,
      button: `Search`,
      tabs: { all: `All`, journals: `Journals`, creators: `Creators` },
      sections: { creators: `Creators`, journals: `Journals` },
      stats: {
        subscribers: `subscribers`,
        journals: `journals`,
        minRead: `min read`,
        likes: `likes`,
      },
      searching: `Searching…`,
      failed: `Search failed. Please try again.`,
      noResults: `No results for`,
      tryDifferent: `Try different keywords or browse by tag`,
      emptyPrompt: `Search for journals, writers, and topics`,
      premium: `Premium`,
    },
    pro: {
      meta: {
        title: `Vellum — Plans & Pricing`,
        description: `Free creator tools, Vellum Pro for audience & monetisation, and Vellum Author for publishing in every format. Pick the plan that fits.`,
      },
      nav: {
        features: `Features`,
        pricing: `Pricing`,
        compare: `Compare`,
        referrals: `Referrals`,
        goToApp: `Go to app →`,
        signIn: `Sign in`,
        startFree: `Start free`,
      },
      hero: {
        badge: `Vellum Pro`,
        headline1: `Write it. Publish it.`,
        headline2: `Own every format.`,
        sub: `Two paid tiers, flat monthly pricing, lower platform fees. Pro gives you the full creator toolkit. Author adds the book-publishing layer — ebook export, custom domain, and content protection.`,
        alreadyPro: `You have Vellum Pro — all Pro features are active on your account.`,
        alreadyAuthor: `You have Vellum Author — every feature is active on your account.`,
        goToJournals: `Go to your journals →`,
        openCalendar: `Open Content Calendar`,
        upgrade: `See plans`,
        seeFeatures: `See all features`,
      },
      pricing: {
        currentPlan: `Your current plan`,
        includedInAuthor: `Included in Author`,
        eyebrow: `Pricing`,
        title: `Flat monthly fee.`,
        titleEm: `Lower platform cut.`,
        sub: `Most platforms take 10%+ of your revenue. Vellum charges 6% — dropping to 5.5% on Pro and 5% on Author — plus a flat monthly plan fee that doesn't scale with what you earn.`,
        monthly: `Monthly`,
        annual: `Annual`,
        saveYear: `Save on annual`,
        free: {
          name: `Free`,
          price: `Always free`,
          note: `No credit card required`,
          startFree: `Start for free`,
          perks: [
            `Unlimited journals & series`,
            `Up to 5 subscription tiers`,
            `Version history (20 saves)`,
            `Basic analytics dashboard`,
            `Stripe payouts`,
            `Real-time reader feed`,
            `Scheduled publishing`,
            `Bookmarks & discovery`,
          ],
        },
        pro: {
          name: `Pro`,
          period: `/mo`,
          billedAnnual: `Billed $216/year`,
          billedMonthly: `Billed monthly, cancel anytime`,
          bestValue: `Most popular`,
          mostPopular: `Most popular`,
          upgrade: `Upgrade to Pro`,
          perks: [
            `Everything in Free`,
            `Email delivery to subscribers`,
            `ARC distribution for beta readers`,
            `Custom bio page sections`,
            `Full engagement analytics + email tracking`,
            `Unlimited version history`,
            `Content calendar`,
            `Manuscript import (Scrivener, Word, Google Docs)`,
            `Co-author collaboration`,
            `Unlimited subscription tiers`,
            `Profile theming`,
            `Pay-per-post (one-time journal purchases)`,
            `Gift subscriptions`,
            `Founding member tiers`,
            `Priority support`,
          ],
        },
        author: {
          name: `Author`,
          period: `/mo`,
          billedAnnual: `Billed $336/year`,
          billedMonthly: `Billed monthly, cancel anytime`,
          bestValue: `Best value`,
          badge: `For book publishers`,
          upgrade: `Upgrade to Author`,
          perks: [
            `Everything in Pro`,
            `Ebook & print export (EPUB 3/2, MOBI, PDF)`,
            `Custom domain`,
            `Enhanced content protection & watermarking`,
            `AI writing tools (coming soon)`,
          ],
        },
        note: `Your plan fee is a flat monthly charge that doesn't scale with your earnings. On top of that, Vellum takes a platform fee on creator revenue — 6% on Free, 5.5% on Pro, 5% on Author — plus Stripe's processing fee (~2.9% + 30¢). The paid plans keep more of what you earn as you grow.`,
      },
      features: {
        eyebrow: `Pro features`,
        title: `Fourteen tools that ship`,
        titleEm: `on day one`,
        sub: `Every Pro feature is built around the same goal: help you publish, reach every reader, and understand what's working.`,
        authorEyebrow: `Author features`,
        authorTitle: `Four tools for`,
        authorTitleEm: `book publishers`,
        authorSub: `Author adds the publishing layer for writers who also sell books — export to every platform, own your domain, and lock down your content.`,
        availableNow: `Available now`,
        learnMore: `Learn more →`,
        launch: `Launch`,
        proTag: `Pro`,
        authorTag: `Author`,
        authorNudge: `Upgrade to Author to unlock these →`,
        items: [
          {
            title: `Email delivery to subscribers`,
            body: `Every publish pushes a styled newsletter to subscriber inboxes — no more relying on readers remembering to check back. Open and click tracking feeds back into your analytics dashboard.`,
          },
          {
            title: `ARC distribution`,
            body: `Send time-limited, watermarked advance copies to beta readers directly from the export panel. Track downloads, collect star ratings and written feedback, and revoke access after your launch date.`,
          },
          {
            title: `Custom bio page sections`,
            body: `Turn your profile into a proper author website. Add a book catalog with covers and buy links, a press & media section with publication credits, and a link tree — all ordered and styled your way.`,
          },
          {
            title: `Engagement analytics`,
            body: `Read-through rate, scroll depth, paywall conversion, subscriber retention curves, and now email open/click rates — the data that actually drives decisions.`,
          },
          {
            title: `Unlimited version history`,
            body: `Free keeps 20 revisions. Pro keeps every save, forever. Roll back to any draft, any edit, any moment in your writing history.`,
          },
          {
            title: `Content calendar`,
            body: `Visual scheduling interface. See your publish cadence at a glance, drag drafts onto dates, and bulk reschedule entire arcs.`,
          },
          {
            title: `Unlimited subscription tiers`,
            body: `Free creators cap at 5 tiers. Pro removes the limit entirely — build any offer stack, any pricing structure you can dream up.`,
          },
          {
            title: `Profile theming`,
            body: `Custom colors, fonts, and layout on your creator profile. Choose from editorial, centered, or magazine layouts and match your brand palette exactly.`,
          },
          {
            title: `Manuscript import`,
            body: `Import directly from Scrivener, Word, and Google Docs — preserving headers, italics, scene breaks, and chapter structure.`,
          },
          {
            title: `Co-author collaboration`,
            body: `Invite editors or co-writers with role-based permissions: edit, comment, or view only. Ghost-writing workflows built in.`,
          },
          {
            title: `Pay-per-post`,
            body: `One-time purchases for individual journals, separate from subscriptions. Capture readers who won't commit to recurring — set a price, they buy once, they own it.`,
          },
          {
            title: `Gift subscriptions`,
            body: `Let readers buy a subscription as a gift. Generates a redemption link the recipient claims at their own pace — no account required to receive.`,
          },
          {
            title: `Founding member tier`,
            body: `Offer early subscribers a legacy discounted rate locked in forever. Mark any tier as founding-member, set an optional slot cap, and reward your earliest supporters.`,
          },
          {
            title: `Priority support`,
            body: `Dedicated support queue with response within one business day. When your income depends on the platform, support matters.`,
          },
        ],
        authorItems: [
          {
            title: `Ebook & print export`,
            body: `Generate validated EPUB 3/2, MOBI, and print-ready PDF/X-1a from any journal or series. Guide readers to buy on Kindle, Kobo, Apple Books, and more.`,
          },
          {
            title: `Custom domain`,
            body: `Publish at yourname.com instead of writevellum.com. Full DNS routing handled by Vellum — no technical setup required.`,
          },
          {
            title: `Enhanced content protection`,
            body: `Visible diagonal watermarks on subscriber-only content tie every view to a specific reader. Invisible zero-width character encoding survives copy-paste, letting you trace any pirated excerpt back to its source.`,
          },
          {
            title: `AI writing tools`,
            body: `Structural feedback, outline generation, chapter summaries, and blurb drafting — built for long-form work, not autocomplete. Coming soon.`,
          },
        ],
      },
      ebookArc: {
        eyebrow: `Ebook & ARC`,
        title: `Every platform.`,
        titleEm: `Every reader.`,
        sub: `From first draft to final launch — export for retail, send ARCs to reviewers, and collect feedback before you go wide.`,
        list: [
          `EPUB 3 & EPUB 2 validated files`,
          `MOBI for Kindle compatibility`,
          `Print-ready PDF/X-1a`,
          `ARC EPUB with reviewer watermark`,
          `Time-limited download links`,
          `Per-reader star rating & text feedback`,
          `Download cap & expiry enforcement`,
          `Automatic title page & table of contents`,
          `Backmatter that links to your next book`,
          `Word-compatible .doc export`,
          `Box sets from completed series`,
          `Update & regenerate any time`,
        ],
        startPublishing: `Start publishing in every format`,
      },
      creatorCode: {
        eyebrow: `Author affiliate program`,
        title: `A code that earns`,
        titleEm: `every time you share it`,
        sub: `Author-tier creators (and platform moderators and admins) get a personal creator code automatically. Share it once, earn forever — every redemption discounts the buyer 5% and credits 5% commission to your token balance.`,
        scopeNote: `Your code works platform-wide for Vellum Pro upgrades and token bundles. For content purchases and creator-tier subscriptions, the code only applies when the buyer is purchasing from you — keeping your code aligned with your audience.`,
        viewMyCode: `View my creator code`,
        upgradeToAuthor: `Upgrade to Author to claim your code`,
        programTerms: `Program terms`,
        benefits: {
          discount: {
            title: `A discount your readers can actually use`,
            body: `Share your unique code anywhere — your newsletter, your bio, your podcast. Anyone who redeems it gets 5% off Vellum Pro, Author, token bundles, or any of your own paid content. It's an instant pitch upgrade.`,
          },
          commission: {
            title: `5% commission, paid in tokens`,
            body: `Every redemption credits 5% of the post-discount price to your token balance. Spend those tokens to tip other creators, unlock pay-per-post journals, or stockpile them — they don't expire for 12 months.`,
          },
          customisable: {
            title: `Customise. Track. Done.`,
            body: `Your code is auto-issued the moment you upgrade — no waitlist, no application. Rename it once to match your handle, then track lifetime redemptions and commission earned right from your settings page.`,
          },
        },
      },
      testimonials: {
        eyebrow: `From our writers`,
        title: `Built for the long game`,
        items: [
          {
            quote: `The email delivery alone was worth upgrading. I used to lose readers between posts because they forgot to check back. Now every publish lands in their inbox.`,
            name: `Yara Okonkwo`,
            role: `Novelist & essayist · 2,100 subscribers`,
            avatar: `YO`,
          },
          {
            quote: `ARC distribution changed my launch process entirely. I sent 30 review copies from inside Vellum, collected feedback before publication day, and had reviews ready the moment I went live.`,
            name: `Thomas Avery`,
            role: `Serialised fiction · 890 subscribers`,
            avatar: `TA`,
          },
          {
            quote: `The book catalog section on my profile page finally makes it look like an author website instead of a blog. Readers can see my whole back catalogue the moment they land on my page.`,
            name: `Priya Mehta`,
            role: `Cultural criticism · 1,440 subscribers`,
            avatar: `PM`,
          },
        ],
      },
      compare: {
        eyebrow: `Compare plans`,
        title: `Everything, side by side`,
        free: `Free`,
        pro: `Pro`,
        author: `Author`,
        note: `Paid features are available immediately on upgrade. Cancel anytime — you'll keep access until the end of your billing period.`,
        creatorCodeFeature: `Creator code program (5% off / 5% commission)`,
        features: [
          `Unlimited journals & series`,
          `Stripe payouts`,
          `Real-time reader feed`,
          `Scheduled publishing`,
          `Subscription tiers`,
          `Version history`,
          `Analytics`,
          `Email delivery to subscribers`,
          `ARC distribution`,
          `Custom bio page sections`,
          `Content calendar`,
          `Manuscript import`,
          `Co-author collaboration`,
          `Profile theming`,
          `Pay-per-post purchases`,
          `Gift subscriptions`,
          `Founding member tier`,
          `Priority support`,
          `Ebook & print export`,
          `Custom domain`,
          `Content protection & watermarking`,
          `AI writing tools`,
        ],
        freeValues: [`Up to 5`, `20 saves`, `Basic`],
        proValues: [`Unlimited`, `Unlimited`, `Full + email`],
      },
      cta: {
        eyebrow: `Ready?`,
        title: `Your writing deserves`,
        titleEm: `a professional home.`,
        sub: `Join the writers publishing everywhere, reaching every inbox, protecting their work, and understanding every reader. Upgrade takes thirty seconds.`,
        upgrade: `Upgrade to Pro — $18/mo`,
        upgradeAuthor: `Upgrade to Author — $28/mo`,
        back: `Back to Vellum`,
      },
      aiTransparency: {
        title: `Transparent by default`,
        body: `Vellum labels AI-assisted content automatically. When you use our AI writing tools — feedback, outlines, story generation — your journal is labeled so readers always know how it was made. Pro members can also self-disclose external AI use in one click. Building trust with your audience starts here.`,
        learnMore: `Our AI disclosure policy →`,
      },
      footer: {
        tagline: `Long-form writing, honestly monetised.`,
        pro: `Plans`,
        platform: `Platform`,
        support: `Support`,
        links: {
          features: `Features`,
          pricing: `Pricing`,
          freeVsPro: `Compare plans`,
          upgrade: `Upgrade`,
          home: `Home`,
          discover: `Discover`,
          referral: `Referral program`,
          getStarted: `Get started`,
          signIn: `Sign in`,
        },
      },
    },
    referral: {
      meta: {
        title: `Referral Program — Vellum`,
        description: `Invite writers and readers to Vellum and earn rewards as they join and publish.`,
      },
      nav: {
        pro: `Pro`,
        dashboard: `Dashboard →`,
        getStarted: `Get started →`,
        signIn: `Sign in`,
      },
      eyebrow: `Referral Program`,
      headline1: `Share Vellum.`,
      headline2: `Earn rewards.`,
      sub: `Invite creators and readers you believe in. When they publish or subscribe, you unlock rewards — from exclusive content to lifetime Pro access.`,
      viewLink: `View your referral link →`,
      getStarted: `Get started free →`,
      signIn: `Sign in`,
      howItWorks: {
        eyebrow: `How it works`,
        title: `Three steps to your first reward`,
        sub: `No complicated dashboards. Share one link, watch it grow.`,
        steps: [
          {
            n: `01`,
            title: `Get your link`,
            body: `Every Vellum account gets a unique referral link from day one — no application required. Find it in your dashboard under Referrals.`,
          },
          {
            n: `02`,
            title: `Share it`,
            body: `Share with writers who'd love a thoughtful publishing home, or readers who'd appreciate an ad-free, premium experience. Your link, your audience.`,
          },
          {
            n: `03`,
            title: `They qualify, you earn`,
            body: `A referral counts once your invite publishes 3 premium articles or subscribes to Vellum Pro — within 90 days of joining. Hit milestones to unlock rewards.`,
          },
        ],
      },
      qualifies: {
        eyebrow: `What qualifies`,
        title: `Two paths to a qualified referral`,
        sub: `We reward growth that matters — active creators and paying subscribers.`,
        creator: {
          title: `Creator path`,
          body: `The person you invite creates a Vellum account and publishes at least 3 premium articles within 90 days. Premium means subscriber-only or paywalled content — free public posts don't count toward the threshold.`,
          note: `Best for: writers, journalists, essayists, or anyone building a paid newsletter.`,
        },
        subscriber: {
          title: `Pro subscriber path`,
          body: `The person you invite creates a Vellum account and subscribes to Vellum Pro within 90 days — monthly or annual.`,
          note: `Best for: avid readers who want unlimited access to premium content and reader-exclusive features.`,
        },
      },
      rewards: {
        eyebrow: `Rewards`,
        title: `Seven milestones.`,
        titleEm: `Increasingly generous rewards.`,
        sub: `Rewards are cumulative and permanent. Once earned, they're yours.`,
        referrals: `referrals`,
        mostPopular: `Most popular`,
        topTier: `Top tier`,
      },
      advocates: {
        eyebrow: `Built for advocates`,
        title: `The best way to grow Vellum is through people who already love it`,
        sub: `Referrals from people who genuinely use a platform convert better, stay longer, and contribute more. That's who we want to reward.`,
        perks: [
          `No expiry on earned rewards — once you hit a milestone, it's permanent`,
          `Transparent tracking — see every referral and its status in real time`,
          `90-day qualification window — plenty of time for your invites to settle in`,
          `Fraud protection — your referrals are verified so the program stays fair`,
        ],
        recentReferrals: `Recent qualified referrals`,
        demoBadges: {
          premiumPosts: `3 premium posts`,
          pro: `Vellum Pro`,
        },
      },
      cta: {
        eyebrow: `Start referring`,
        title: `Your referral link is waiting`,
        sub: `Join Vellum and find your unique link in the dashboard. It only takes a moment — and your first reward is just 3 referrals away.`,
        dashboard: `Go to your dashboard →`,
        createAccount: `Create your free account →`,
      },
      footer: {
        tagline1: `A home for thoughtful writing.`,
        tagline2: `Ad-free. Creator-first.`,
        product: `Product`,
        account: `Account`,
        legal: `Legal`,
        links: {
          home: `Home`,
          pro: `Vellum Pro`,
          referrals: `Referrals`,
          signIn: `Sign in`,
          createAccount: `Create account`,
          dashboard: `Dashboard`,
          privacy: `Privacy`,
          terms: `Terms`,
          referralPolicy: `Referral policy`,
        },
      },
    },
    creatorDashboard: {
      meta: { title: `Creator Dashboard`, description: `Manage your Vellum creator account.` },
      becomeMeta: { title: `Become a Creator`, description: `Set up your Vellum creator account.` },
      title: `Creator dashboard`,
      newJournal: `New journal`,
      tabs: {
        overview: `Overview`,
        subscribers: `Subscribers`,
        revenue: `Revenue`,
        tiers: `Tiers`,
        series: `Series`,
        referrals: `Referrals`,
        analytics: `Analytics`,
        analyticsPro: `Analytics`,
        profileSections: `Profile sections`,
      },
      become: {
        title: `Become a creator`,
        blurb: `Set up subscription tiers, publish gated journals, and earn directly from your readers. All identity and tax information is encrypted and handled securely through Stripe.`,
        steps: [
          { icon: ``, text: `Create your creator profile on Vellum` },
          { icon: ``, text: `Verify your identity (government-issued ID via Stripe)` },
          { icon: ``, text: `Submit tax info (W-9 / W-8BEN) — encrypted at rest and in transit` },
          { icon: ``, text: `Connect a bank account for payouts` },
        ],
        activate: `Activate creator account`,
        activating: `Setting up…`,
        blurb2: `Accept paid subscriptions, manage tiers, and get paid directly — all from one place. Setup takes about two minutes.`,
        stepsDetailed: {
          profile: {
            title: `Create your creator profile`,
            body: `Your account is activated instantly — no review required.`,
          },
          identity: {
            title: `Verify your identity & tax info via Stripe`,
            body: `You'll be redirected to Stripe to submit your legal name, date of birth, and W-9 / W-8 tax details. Vellum never sees or stores this data — it goes directly to Stripe over an encrypted connection.`,
          },
          bank: {
            title: `Add a payout bank account`,
            body: `Link your bank during Stripe onboarding. Payouts are deposited automatically on a rolling basis once subscribers pay.`,
          },
          tiers: {
            title: `Set up subscription tiers`,
            body: `Name your tiers, set prices, and define subscriber benefits — all from your creator dashboard.`,
          },
        },
        getStartedCta: `Get started — it's free`,
        securityNote: `Identity and tax data is encrypted in transit and stored securely by Stripe. Vellum is never in possession of your documents or bank details.`,
      },
      stripe: {
        connected: {
          title: `Stripe payouts connected`,
          sub: `Identity and tax info verified. Payouts are enabled on your account.`,
          btn: `Stripe dashboard ↗`,
        },
        notConnected: {
          title: `Connect Stripe to receive payouts`,
          sub: `Complete identity verification and tax info to start earning. Data is encrypted and transmitted securely via Stripe Connect.`,
          btn: `Set up payouts`,
        },
        opening: `Opening…`,
      },
      overview: {
        totalJournals: `Total journals`,
        published: `Published`,
        totalViews: `Total views`,
        totalLikes: `Total likes`,
        activeSubscribers: `Active subscribers`,
        totalEarnings: `Total earnings`,
        topJournals: `Top journals`,
        moderation: `Content moderation`,
        viewReports: `View reports`,
        reportsNote: `If any of your journals receive content reports they will appear on your reports page. You can review the moderation status and submit an appeal if you believe a decision was made in error.`,
        reportsLink: `reports page`,
        reportsHintBody: `If any of your journals receive content reports they will appear on your <1>reports page</1>. You can review the moderation status and submit an appeal if you believe a decision was made in error.`,
      },
      subscribers: {
        title: `Subscribers`,
        active: `active`,
        lapsed: `lapsed`,
        exportCSV: `Export CSV`,
        exporting: `Exporting…`,
        activeTab: `Active`,
        lapsedTab: `Lapsed`,
        loading: `Loading subscribers…`,
        noActive: {
          title: `No active subscribers yet`,
          hint: `Share your profile to attract readers`,
        },
        noLapsed: {
          title: `No lapsed subscribers`,
          hint: `All your subscribers are still active`,
        },
        since: `Since`,
        cancelled: `Cancelled`,
        accessUntil: `Access until`,
      },
      revenue: {
        mrr: `Monthly recurring revenue`,
        available: `Available balance`,
        allTime: `All-time earnings`,
        last12: `Revenue — last 12 months`,
        byTier: `Revenue by tier`,
        subscriber: `subscriber`,
        subscribers: `subscribers`,
        loading: `Loading revenue data…`,
        noData: {
          title: `No revenue data yet`,
          hint: `Revenue will appear here once you have paying subscribers`,
        },
        payoutsEnabled: {
          title: `Payouts enabled`,
          sub: `Your Stripe Connect account is verified. Open the dashboard to manage payouts and tax forms.`,
        },
        payoutsDisabled: {
          title: `Connect Stripe to receive payouts`,
          sub: `Identity verification and tax information required to enable payouts. All data is encrypted in transit.`,
        },
      },
      tiers: {
        title: `Subscription tiers`,
        addTier: `+ Add tier`,
        cancel: `Cancel`,
        limitNudge: `{count}/{max} tiers used.`,
        upgradePro: `Upgrade to Pro`,
        unlimited: `for unlimited.`,
        dragHint: `Drag tiers to reorder. Tiers at the top grant the most access — subscribers on a higher tier can always read content gated at a lower tier.`,
        tierName: `Tier name`,
        tierNamePlaceholder: `e.g. Supporter`,
        price: `Price ($/mo)`,
        pricePlaceholder: `5.00`,
        description: `Description`,
        descriptionPlaceholder: `What subscribers get…`,
        foundingMember: {
          label: `Founding member tier`,
          hint: `Lock this price forever for early subscribers. Once a reader subscribes at this rate, it never changes for them.`,
          alreadyHave: `You already have a founding member tier. Deactivate it first to create a new one.`,
          slots: `Founding member spots`,
          slotsHint: `(leave blank for unlimited)`,
          slotsCap: `e.g. 100`,
        },
        createTier: `Create tier`,
        creating: `Creating…`,
        noTiers: {
          title: `No tiers yet`,
          hint: `Create a tier above to start earning from subscribers`,
        },
        accessLabels: {
          highest: `Highest access — unlocks all content`,
          base: `Base tier — lowest access level`,
          above: `Access to content for this tier and {n} tier above`,
          abovePlural: `Access to content for this tier and {n} tiers above`,
        },
        edit: `Edit`,
        deactivate: `Deactivate`,
        save: `Save`,
        saving: `Saving…`,
        priceNote: `$/mo`,
        slotsMax: `spots max`,
        founding: `Founding`,
        confirmDeactivate: {
          title: `Deactivate "{name}"?`,
          body: `Existing subscribers will keep access until the end of their current billing period. You can't publish new content gated to a deactivated tier, but no one loses access immediately.`,
          cancel: `Cancel`,
          confirm: `Deactivate tier`,
          confirming: `Deactivating…`,
        },
        errors: {
          nameRequired: `Tier name required`,
          minPrice: `Minimum price is $0.99`,
        },
        success: {
          created: `Tier created`,
          updated: `Tier updated`,
          deactivated: `Tier deactivated`,
          reordered: `Tier order saved`,
        },
        aria: {
          tierPrice: `Tier price`,
          tierDescription: `Tier description`,
          foundingSlots: `Founding member slot cap`,
          dragHandle: `Drag to reorder`,
          editTierName: `Edit tier name`,
          editTierPrice: `Edit tier price`,
          editTierDescription: `Edit tier description`,
        },
        priceLockedTitle: `Price is locked for founding member tiers with active subscribers`,
      },
      series: { loading: `Loading series manager…` },
      analytics: { loading: `Loading analytics…` },
      profileSections: {
        title: `Profile sections`,
        hint: `Add a book catalog, press mentions, and a link tree to your public profile page. Sections appear below your series list and above your journals feed.`,
      },
      errors: {
        loadDashboard: `Failed to load dashboard`,
        loadSubscribers: `Failed to load subscribers`,
        loadRevenue: `Failed to load revenue data`,
        exportCSV: `Failed to export CSV`,
        stripeConnect: `Stripe Connect is not yet enabled on this Stripe account. Visit dashboard.stripe.com/connect to enroll, then try again.`,
        stripeOnboarding: `Failed to start Stripe onboarding`,
        stripeDashboard: `Failed to open Stripe dashboard`,
        tierCreate: `Failed to create tier`,
        tierUpdate: `Failed to update tier`,
        tierDeactivate: `Failed to deactivate tier`,
        tierReorder: `Failed to save tier order`,
        activateCreator: `Failed to activate creator account`,
        stripeComplete: `Stripe verification complete — your payouts account is being activated.`,
        stripeExpired: `Stripe onboarding session expired — please try again.`,
        stripeNotEnabled: `Stripe Connect is not yet enabled on this Stripe account.`,
      },
      success: {
        stripeSetup: `Stripe setup complete — your payout account is connected.`,
        creatorActivated: `Creator account activated! To enable payouts, enroll in Stripe Connect at dashboard.stripe.com/connect, then use "Set up payouts" from the Revenue tab.`,
        creatorRedirecting: `Creator account created — redirecting to Stripe for verification…`,
      },
    },
    journals: {
      home: {
        meta: {
          title: `Feed`,
          description: `Discover long-form writing from creators you follow and beyond.`,
        },
        tabs: {
          latest: `Latest`,
          trending: `Trending`,
          following: `Following`,
          forYou: `For You`,
        },
        personalisedNote: `Personalize your feed by following more creators.`,
        cardView: `Card view`,
        listView: `List view`,
        heading: `Journals`,
        subheading: `Long-form stories, guides, and insights from creators`,
        personalisedBadge: `Personalised based on your reading history`,
        personaliseHint: `Like and dislike journals to personalize your feed`,
        humanOnly: {
          chip: `Human only`,
          activeTooltip: `Showing human-written journals only. Click to show all.`,
          inactiveTooltip: `Filter to human-written journals only`,
        },
        feedLayoutAria: `Feed layout`,
        cardGridViewAria: `Card grid view`,
        loadingJournalsAria: `Loading journals`,
        tryAgain: `Try Again`,
        writeJournal: `Write a Journal`,
        emptyFollowing: `Creators you follow haven't published any journals yet.`,
        emptyDefault: `Be the first to share your story!`,
      },
      myJournals: {
        meta: { title: `My Journals` },
        title: `My journals`,
        newJournal: `New journal`,
        status: { published: `Published`, draft: `Draft` },
        scheduledToday: `Publishing today`,
        scheduledTomorrow: `Publishing tomorrow`,
        scheduledDays: `Publishing in {n} days`,
        noJournals: { title: `No journals yet`, hint: `Start writing your first journal` },
        collab: `Collaboration`,
        loading: `Loading…`,
        tabs: {
          all: `All`,
          published: `Published`,
          drafts: `Drafts`,
          scheduled: `Scheduled`,
          collaborating: `Collaborating`,
        },
        filterByStatus: `Filter stories by status`,
        yourStories: `Your stories`,
        loadingStories: `Loading stories`,
        loadingMoreStories: `Loading more stories`,
        calendarButton: `Calendar`,
        calendarProTooltip: `Content Calendar — Pro feature`,
        actions: {
          viewPublished: `View published`,
          viewPublishedAria: `View published version of "{{title}}"`,
          edit: `Edit`,
          editAria: `Edit "{{title}}"`,
          exportAuthor: `Export ebook or print (Author)`,
          exportAria: `Export "{{title}}"`,
          deleteAria: `Delete "{{title}}"`,
          openInEditor: `Open in editor`,
          readAria: `Read "{{title}}"`,
        },
        delete: {
          title: `Delete journal?`,
          body: `This action cannot be undone.`,
          confirm: `Delete`,
          deleting: `Deleting…`,
          cancel: `Cancel`,
          modalBody: `<1>{{title}}</1> will be permanently deleted. This cannot be undone.`,
          untitledFallback: `Untitled`,
        },
        collaborating: {
          collabListAria: `Journals you collaborate on`,
          emptyHint: `When someone invites you to co-write or edit their journal, it will appear here.`,
          retry: `Retry`,
          seriesSection: `Series`,
          byAuthor: `by {{author}}`,
        },
        newInteractive: `New interactive story`,
        newInteractiveDescription: `Choose-your-own-adventure with branching scenes.`,
        standardJournal: `Standard journal`,
        standardJournalDescription: `A long-form story or essay.`,
        moreJournalTypes: `More journal types`,
      },
      cyoa: {
        // ── Card / list badges ──
        badge: { interactive: `Interactive` },

        // ── Editor ──
        editor: {
          loadingStory: `Loading interactive story`,
          loadError: `Could not load story.`,
          noScenesPlaceholder: `Setting up your story… give your interactive journal a title to get started.`,
          noSceneSelected: `No scene selected. Add one to get started.`,
          storyStateButton: `Story state ({{vars}} vars · {{items}} items)`,

          // Sidebar
          sidebar: {
            scenesHeading: `Scenes`,
            addScene: `Add scene`,
            addSceneAria: `Add scene`,
            sceneActionsAria: `Scene "{{title}}" actions`,
            startingSceneTooltip: `Starting scene`,
            endingTooltip: `Ending`,
            markAsStart: `Mark as starting scene`,
            moveUp: `Move up`,
            moveDown: `Move down`,
            deleteScene: `Delete scene`,
            untitled: `(untitled)`,
          },

          // New scene dialog
          newScene: {
            title: `New scene`,
            titleField: `Title`,
            titleHelp: `Shown in the sidebar — readers won't see this.`,
            keyField: `Key`,
            keyHelp: `Stable identifier for links. Lowercase, e.g. "forest_left".`,
            keyInvalid: `Use lowercase letters, numbers, underscores. Must start with a letter.`,
            keyDuplicate: `A scene with this key already exists.`,
            titleTooLong: `Too long.`,
            isEnding: `Mark as ending scene`,
            create: `Create`,
          },

          // Scene editor
          scene: {
            sceneTitleAria: `Scene title`,
            sceneTitlePlaceholder: `Scene title`,
            startBadge: `Start`,
            keyBadge: `key: {{key}}`,
            isEndingToggle: `Ending`,
            endingLabel: `Ending label`,
            endingLabelPlaceholder: `e.g. Bittersweet`,
            saving: `Saving…`,
            saved: `Saved`,
            startingSceneHint: `This is the starting scene — write the opening prose here. Readers see this first.`,
            bodyPlaceholder: `Write what happens in this scene…`,
          },

          // Choices panel
          choices: {
            heading: `Choices`,
            addChoice: `Add choice`,
            endingNotice: `This is an ending. The reader will see a "Start over" button instead of choices.`,
            emptyHint: `No choices yet. Add at least one to let the story continue, or mark this scene as an ending.`,
            missingScene: `(missing scene #{{id}})`,
            missingSceneFallback: `Scene #{{id}}`,
            arrowToScene: `→ {{title}}`,
            endingChip: `ending`,
            conditionChip: `condition`,
            conditionHiddenTooltip: `Hidden when condition fails`,
            conditionLockedTooltip: `Locked when condition fails`,
            effectChip_one: `{{count}} effect`,
            effectChip_other: `{{count}} effects`,
            effectChipTooltip: `{{count}} effect(s) on click`,
            moveUpAria: `Move choice up`,
            moveDownAria: `Move choice down`,
            editAria: `Edit choice`,
            deleteAria: `Delete choice`,
          },

          // Choice edit dialog
          choiceDialog: {
            titleEdit: `Edit choice`,
            titleAdd: `Add choice`,
            labelField: `Choice text`,
            labelPlaceholder: `What the reader sees on the button`,
            labelCount: `{{used}}/{{max}}`,
            targetField: `Goes to scene`,
            targetLoopBack: `(loop back to here)`,
            targetStartSuffix: `· start`,
            targetEndingSuffix: `· ending`,
            whenConditionFails: `When condition fails`,
            showAsLocked: `Show as locked instead of hiding`,
            lockedReasonField: `Locked-state reason (shown to reader)`,
            lockedReasonPlaceholder: `e.g. Locked: needs the brass key`,
            saveButton: `Save`,
            addButton: `Add choice`,
          },

          // Condition builder
          condition: {
            heading: `Show only if`,
            allOf: `All of`,
            anyOf: `Any of`,
            alwaysShown: `Always shown.`,
            addVariableCheck: `Variable check`,
            addItemCheck: `Item check`,
            needVarsHint: `Add a variable in the State drawer first`,
            needItemsHint: `Add an item in the State drawer first`,
            needBothHint: `Add variables or items in the State drawer to use conditions.`,
            removeClauseAria: `Remove clause`,
            opHas: `has`,
            opLacks: `lacks`,
          },

          // Effect editor
          effects: {
            heading: `Effects when picked`,
            none: `None — picking this choice doesn't change reader state.`,
            setVariable: `Set variable`,
            adjustNumber: `Adjust number`,
            giveItem: `Give item`,
            takeItem: `Take item`,
            verbSet: `Set`,
            verbAdd: `Add`,
            verbGive: `Give`,
            verbTake: `Take`,
            wordTo: `to`,
            removeAria: `Remove effect`,
            needNumericVarHint: `Add a numeric variable first`,
          },

          // State schema drawer
          stateDrawer: {
            title: `Story state`,
            close: `Close`,
            tabVariables: `Variables ({{count}})`,
            tabItems: `Items ({{count}})`,
            saveVariables: `Save variables`,
            saveItems: `Save items`,
            saveError: `Could not save.`,
            variablesEmpty: `No variables yet. Variables track reader state — courage, met_baron, etc. — and feed conditions and effects on choices.`,
            itemsEmpty: `No items yet. Items represent things the reader can carry — keys, weapons, clues — and can be granted, removed, or required by conditions.`,
            varName: `Name`,
            varNameHelp: `lowercase, letters/digits/_`,
            varType: `Type`,
            varInitial: `Initial`,
            varLabel: `Display label (optional)`,
            removeVarAria: `Remove variable`,
            addVariable: `Add variable`,
            varMaxTooltip: `Maximum {{max}} variables`,
            itemKey: `Key`,
            itemDisplayName: `Display name`,
            itemDescription: `Description (optional)`,
            removeItemAria: `Remove item`,
            addItem: `Add item`,
            itemMaxTooltip: `Maximum {{max}} items`,
          },

          // Toast / inline errors
          errors: {
            createScene: `Could not create scene.`,
            updateScene: `Could not update scene.`,
            saveSceneContent: `Could not save scene content.`,
            deleteScene: `Could not delete scene.`,
            setStartScene: `Could not set start scene.`,
            reorderScenes: `Could not reorder scenes.`,
            addChoice: `Could not add choice.`,
            updateChoice: `Could not update choice.`,
            deleteChoice: `Could not delete choice.`,
            reorderChoices: `Could not reorder choices.`,
            saveVariables: `Could not save variables.`,
            saveItems: `Could not save items.`,
          },
        },

        // ── Reader ──
        reader: {
          loading: `Loading interactive story`,
          loadError: `Could not load story.`,
          noScenes: `This story has no scenes yet.`,
          stepLabel: `Step {{n}}`,
          endingSuffix: ` · Ending`,
          inventoryChip: {
            tooltip: `Story state`,
            stateAndItems: `{{vars}} state · {{items}} item`,
            stateAndItems_other: `{{vars}} state · {{items}} items`,
            stateOnly: `{{vars}} state`,
            zeroItems: `0 items`,
            variablesHeading: `Variables`,
            inventoryHeading: `Inventory`,
            inventoryEmpty: `Empty.`,
            unknown: `—`,
            yes: `yes`,
            no: `no`,
          },
          history: {
            title: `Your path`,
            close: `Close history`,
            description: `Tap any earlier scene to rewind there. Your inventory and any progress will be reset to how things were at that moment.`,
            startLabel: `Start`,
            stepLabel: `Step {{n}}`,
            missingScene: `(missing scene)`,
            openTooltip: `Your path through the story`,
            openAria: `Open history`,
            restartTooltip: `Restart from the beginning`,
            restartAria: `Restart story`,
          },
          scene: {
            empty: `(This scene is empty.)`,
            deadEnd: `The story stops here. Open the history drawer or restart to try a different path.`,
            endingHeading: `Ending`,
            endingBody: `You've reached the end of this path.`,
            startOver: `Start over`,
          },
        },
      },
      drafts: {
        meta: { title: `Drafts` },
        title: `Drafts`,
        titleHeader: `Your Drafts`,
        noDraftsTitle: `No drafts yet`,
        noDraftsHint: `Start writing and your drafts will appear here.`,
        noDrafts: { title: `No drafts`, hint: `Your unpublished journals will appear here` },
        delete: {
          title: `Delete draft?`,
          body: `This action cannot be undone.`,
          confirm: `Delete`,
          deleting: `Deleting…`,
          cancel: `Cancel`,
        },
      },
      bookmarks: {
        meta: { title: `Bookmarks`, description: `Your bookmarked journals on Vellum.` },
        title: `Bookmarks`,
        noBookmarks: { title: `No bookmarks yet`, hint: `Save journals to read later` },
        loadMore: `Load more`,
        loading: `Loading…`,
        titleHeader: `Bookmarked Journals`,
        emptyHint: `Bookmark journals you want to read later and they'll appear here.`,
      },
      calendar: {
        meta: { title: `Content Calendar` },
        title: `Content calendar`,
        setPublishTime: `Set publish time`,
        cancel: `Cancel`,
        unscheduledDrafts: `Unscheduled drafts`,
        errors: {
          cantReschedule: `Published journals cannot be rescheduled.`,
          rescheduleError: `Could not reschedule. Please try again.`,
        },
        allCaughtUp: `All caught up!`,
        dragHint: `Drag onto a date/time slot to schedule`,
        untitled: `Untitled`,
        wordsLabel: `words`,
        proBadge: `Pro`,
        proGate: {
          title: `Content Calendar is a Pro feature`,
          body: `Plan your publishing schedule with month, week, and day views with 15-minute scheduling precision.`,
        },
        views: {
          month: `Month`,
          week: `Week`,
          day: `Day`,
          monthAria: `Month view`,
          weekAria: `Week view`,
          dayAria: `Day view`,
        },
      },
      collaborations: {
        meta: { title: `My Collaborations` },
        title: `My Collaborations`,
        loading: `Loading collaborations`,
        error: `Could not load your collaborations. Please try again.`,
        none: `No collaborations yet`,
        journalsSection: `Journals ({n})`,
        roles: { editor: `Editor`, commenter: `Commenter`, viewer: `Viewer` },
        status: { draft: `Draft` },
        collabsDescription: `Journals and series you've been invited to contribute to.`,
        collabsEmptyHint: `When someone invites you to co-write or review their journal or series, it will appear here.`,
        collabsNote: `Only accepted invitations are shown. Pending invitations appear in your notifications.`,
      },
      reports: {
        meta: { title: `My Reports` },
        title: `My Reports`,
        loading: `Loading…`,
        none: { title: `No reports`, hint: `None of your journals have been reported` },
        editJournal: `Edit journal →`,
        editFirst: `Edit journal first`,
        appealPlaceholder: `Explain why you believe this decision was made in error…`,
        labels: {
          reportType: `Report type`,
          reported: `Reported`,
          modNote: `Mod note`,
          yourAppeal: `Your appeal`,
          submit: `Submit appeal`,
        },
        pending: `Your appeal is pending review by our team.`,
        errors: {
          tooShort: `Please provide at least 20 characters explaining your appeal.`,
          failed: `Failed to submit appeal. Please try again.`,
        },
        howAppeals: `How appeals work`,
        metaTitle: `Content reports — Vellum`,
        pageSubLoading: `Loading…`,
        pageSubNone: `None of your journals have been reported.`,
        pageSubCount_one: `{{count}} journal with reports`,
        pageSubCount_other: `{{count}} journals with reports`,
        pageSubHidden: `· {{count}} hidden`,
        pageSubAppealPending_one: `· {{count}} appeal pending`,
        pageSubAppealPending_other: `· {{count}} appeals pending`,
        loadError: `Failed to load your reports. Please refresh and try again.`,
        emptyStateBody: `None of your journals have been reported. Keep up the great work.`,
        hiddenFromReaders: `Hidden from readers`,
        appealPrefix: `Appeal:`,
        appeal: {
          successBody: `Your appeal has been submitted. We'll review it and notify you of the outcome.`,
          reviewWindow: `Appeals are reviewed by our moderation team within 3–5 business days.`,
          submit: `Submit appeal`,
          submitting: `Submitting…`,
          submitNew: `Submit a new appeal`,
          guidanceTail: `Be clear and factual. If relevant, consider updating your journal to address the issue before submitting.`,
          pendingHiddenNotice: `This report is pending review. Your content has been hidden from readers while the moderation team assesses it. You may submit an appeal if you believe this was made in error.`,
          hiddenNotice: `This journal has been hidden following a content report. You may submit an appeal if you believe this decision was made in error.`,
          appealCta: `Appeal this decision`,
          approved: `Appeal approved — your content has been restored.`,
          denied: `Your appeal was not approved.`,
          deniedReason: `Reason: {{reason}}`,
        },
      },
      byTag: {
        loading: `Loading journals…`,
        error: `Failed to load journals. Please try again.`,
        none: `No journals found with this tag.`,
        byTagTryAgain: `Try Again`,
        backToJournals: `Back to journals`,
      },
      series: {
        loading: `Loading series…`,
        loadingAria: `Loading series`,
        exportBoxSet: `Export box set`,
        emptyBody: `No published journals in this series yet.`,
        error: `Series not found or could not be loaded.`,
        invalidId: `Invalid series ID.`,
        backToJournals: `Back to Journals`,
      },
      editor: {
        meta: { title: `Editor` },
        loading: `Loading editor…`,
        saving: `Saving…`,
        saved: `Saved`,
        publish: `Publish`,
        unpublish: `Unpublish`,
        saveDraft: `Save draft`,
        preview: `Preview`,
        settings: `Settings`,
        close: `Close`,
        titlePlaceholder: `Journal title…`,
        subtitlePlaceholder: `Add a subtitle…`,
        bodyPlaceholder: `Write your story…`,
        hiddenByReport: `This journal has been hidden from public view following a content report. It is currently under moderator review. You may continue editing; if you believe this was reported in error, you can submit an appeal from your journal's settings.`,
        ytEmbed: {
          title: `Embed YouTube video`,
          urlLabel: `YouTube URL`,
          urlPlaceholder: `https://youtube.com/watch?v=…`,
          urlError: `Enter a valid YouTube URL (youtube.com or youtu.be)`,
          cancel: `Cancel`,
          embed: `Embed`,
        },
        editorNotFound: `Journal not found`,
        editorBackToStories: `Back to My Stories`,
        editorUploading: `Uploading image…`,
        editorLoadingAria: `Loading journal`,
        editorBodyPlaceholder: `Tell your story…`,
      },
      reader: {
        loading: `Loading…`,
        backToJournals: `Back to journals`,
        edit: `Edit`,
        history: `History`,
        subscribe: `Subscribe to read`,
        gated: `This content is for subscribers only.`,
        listenHint: `Listen`,
        bookmarkAdd: `Bookmark`,
        bookmarkRemove: `Bookmarked`,
        share: `Share`,
        comments: `Comments`,
        likes: `likes`,
        minRead: `min read`,
        startFromBeginning: `Start from the beginning`,
        latestDraft: `Latest Draft`,
        published: `Published`,
        viewAndAppeal: `View & Appeal`,
        viewingDraftAlert: `You're viewing unpublished edits. Switch to "Published" to see the live version.`,
        notFound: {
          meta: `Journal not found`,
          title: `Journal not found`,
          bodyAnon: `This journal may require an account to view.`,
          bodyAuthed: `This journal may have been removed or is not publicly available.`,
          goHome: `Go to Home`,
          logIn: `Log in`,
        },
        continueListening: `Continue listening`,
        continueListeningFrom: `From {{progress}}%`,
        withContributionsFrom: `With contributions from`,
        roles: {
          editor: `Editor`,
          commenter: `Commenter`,
          viewer: `Viewer`,
        },
        follow: `Follow`,
        following: `Following`,
        premium: `Premium`,
        whatDoesThisMean: `What does this mean?`,
        reportThis: `Report this journal`,
        articleNavigation: `Article navigation`,
        viewModeLabel: `View mode`,
        paywall: {
          title: `Continue reading`,
          body: `Create a free account to read the full article, leave comments, and join the conversation.`,
          signUp: `Sign up — it's free`,
          logIn: `Log in`,
        },
        partOfSeries: `Part of a series`,
        viewSeriesAria: `View series: {{title}}`,
        hiddenByReport: {
          title: `This journal has been hidden`,
          body: `A content report has been reviewed and this journal is no longer visible to readers. You can appeal this decision from your reports page.`,
        },
      },
      outline: {
        title: `Sections`,
        ariaLabel: `Document outline`,
        empty: `No sections yet — add a heading to start building an outline.`,
        untitledSection: `Untitled section`,
        openAria: `Open document outline`,
        closeAria: `Close document outline`,
      },
    },
    ebookExport: {
      meta: {
        title: `Ebook & Print Export — Vellum Pro`,
        description: `Generate validated EPUB 3/2, MOBI, and print-ready PDF/X-1a. Publish to Kindle, Kobo, Apple Books, and more — all from one workflow.`,
      },
      nav: {
        signIn: `Sign in`,
        startFree: `Start free`,
      },
      breadcrumb: {
        pro: `Vellum Pro`,
        label: `Ebook & Print Export`,
      },
      eyebrow: `Pro Feature`,
      headline1: `Every platform.`,
      headline2: `One workflow.`,
      hero: {
        sub: `Generate validated EPUB 3/2, MOBI, and print-ready PDF/X-1a — all from your Vellum manuscript. Guide every reader to buy your next book in their favourite store.`,
      },
      upgradeBtn: `Upgrade to Pro`,
      seePlatforms: `See all platforms`,
      formatBadges: {
        epub3: `EPUB 3`,
        epub2: `EPUB 2`,
        ace: `ACE Accessible`,
        arcReady: `ARC Ready`,
      },
      sections: {
        platforms: {
          eyebrow: `Store-ready files`,
          title: `Create ebooks for`,
          titleEm: `every platform`,
          sub: `Each specialised file includes backmatter that guides readers to buy your next book in their favourite store — on Kindle, Kobo, Apple Books, and beyond.`,
          items: [
            {
              name: `Kindle`,
              desc: `MOBI and KFX-compatible EPUB 3 output, optimised for Kindle's rendering engine. Backmatter links guide readers straight to your author page on Amazon.`,
              tags: [`MOBI`, `EPUB 3`, `KDP Ready`],
            },
            {
              name: `Kobo`,
              desc: `Clean EPUB 3 with Kobo-specific reading experience adjustments. Cover metadata and series information formatted to appear correctly in the Kobo storefront.`,
              tags: [`EPUB 3`, `Series metadata`],
            },
            {
              name: `Apple Books`,
              desc: `Apple Books-validated EPUB 3 with iBooks Author conventions respected. Accessibility markup ensures your book passes Apple's distribution requirements.`,
              tags: [`EPUB 3`, `ACE Validated`],
            },
            {
              name: `Google Play Books`,
              desc: `Structured EPUB 2/3 output that meets Google's content policy requirements. Optimised image compression keeps files under the Play Books upload limits.`,
              tags: [`EPUB 2 + 3`, `Compressed`],
            },
            {
              name: `Barnes & Noble`,
              desc: `Nook-compatible EPUB 2 and EPUB 3 files. Font embedding and table-of-contents structure match B&N's distribution expectations out of the box.`,
              tags: [`EPUB 2`, `EPUB 3`, `Nook`],
            },
            {
              name: `Draft2Digital & Smashwords`,
              desc: `Wide-distribution-ready EPUB 2 that satisfies the strictest aggregator validators. Upload once and reach Scribd, Overdrive, Tolino, and dozens more.`,
              tags: [`EPUB 2`, `Wide distribution`],
            },
          ],
          deviceLabels: [`Kindle`, `iPad / Apple Books`, `Kobo / Android`],
        },
        boxSets: {
          eyebrow: `Series collections`,
          title: `Completed a series?`,
          titleEm: `Build the box set.`,
          sub: `Combine every book in a series into a single box-set edition in just a few steps. Readers get one file, one purchase, one download — and you unlock the premium pricing that box sets command.`,
          checkList: [
            `Merge any combination of books from your Vellum library`,
            `Unified table of contents spanning all volumes`,
            `Custom box-set cover and spine design`,
            `Series-specific backmatter linking to individual volumes`,
            `Generates EPUB 3, EPUB 2, MOBI, and print PDF simultaneously`,
          ],
        },
        validated: {
          eyebrow: `Every file, validated`,
          title: `No rejections.`,
          titleEm: `No surprises.`,
          items: [
            {
              badge: `EPUB 3`,
              title: `EPUB 3 — Current standard`,
              desc: `Full reflowable layout with HTML5 and CSS3. Passes EPUBCheck 5.x validation before every export.`,
            },
            {
              badge: `EPUB 2`,
              title: `EPUB 2 — Broadest compatibility`,
              desc: `Legacy format for older devices and aggregators that don't yet support EPUB 3. Auto-generated alongside the EPUB 3 file.`,
            },
            {
              badge: `MOBI`,
              title: `MOBI — Kindle native`,
              desc: `Generated from the same source as your EPUB. Keeps formatting consistent between Amazon and all other stores.`,
            },
            {
              badge: `ACE`,
              title: `ACE Accessibility — Daisy Certified`,
              desc: `Every export passes the DAISY Consortium's Ace accessibility checker. Alt text, reading order, and landmark navigation included.`,
            },
            {
              badge: `ARC`,
              title: `ARCs — Beta reader copies`,
              desc: `Time-limited EPUB and MOBI files for beta readers. Watermarked with reviewer name to trace leaks.`,
            },
            {
              badge: `PDF/X`,
              title: `PDF/X-1a — Print-ready`,
              desc: `Spec-compliant PDF/X-1a with CMYK colour conversion, embedded fonts, bleed, and crop marks — exactly what your printer needs.`,
            },
          ],
          accessibility: {
            title: `ACE-approved accessible output`,
            sub: `Every Vellum ebook passes the DAISY Consortium's Ace accessibility checker. Proper alt text, reading order, and landmark navigation are built in automatically — so your work is readable by every reader, on every device.`,
            upgradeBtn: `Upgrade for access`,
          },
        },
        print: {
          eyebrow: `Painless print`,
          title: `Choose your trim size.`,
          titleEm: `Vellum does the rest.`,
          sub: `Automatic margins, page numbers, headers and footers — set up correctly for print the moment you choose your size. Vellum even handles widow control and spread balancing for a result that looks professionally typeset every time.`,
          checkList: [
            `Paperback and hardcover supported`,
            `24 trim sizes including large print`,
            `Full-bleed pages and custom backgrounds`,
            `Professional-grade PDF/X-1a output`,
          ],
          pdfLabel: `PDF/X-1a`,
        },
        workflow: {
          eyebrow: `How it works`,
          title: `Five steps.`,
          titleEm: `Every format.`,
          steps: [
            {
              number: `01`,
              title: `Build`,
              body: `Import your manuscript and Vellum builds everything automatically — title page, table of contents, chapter breaks, and section dividers.`,
            },
            {
              number: `02`,
              title: `Style`,
              body: `Browse Book Styles to find your match. Your selection applies instantly across every chapter, even ones added later.`,
            },
            {
              number: `03`,
              title: `Preview`,
              body: `See exactly how your ebook appears on Kindle, Kobo, iPad, and iPhone — and how the print edition looks on paper.`,
            },
            {
              number: `04`,
              title: `Generate`,
              body: `Generate ebooks and your print edition in one step. Every format stays in sync with your current manuscript.`,
            },
            {
              number: `05`,
              title: `Update`,
              body: `Typo fixed? New backmatter? Return to Vellum any time — you'll always have a fresh, up-to-date set of files.`,
            },
          ],
          changeNote: {
            title: `Need to make a change?`,
            sub: `Return to Vellum whenever you need to fix a typo, add new backmatter, or update your platform links. Every edit regenerates a fresh, complete set of files — ebooks and print edition together, always in sync.`,
            btn: `Start exporting`,
          },
        },
        import: {
          eyebrow: `Manuscript import`,
          title: `Start from where`,
          titleEm: `you already write.`,
          sub: `Import directly from Scrivener, Microsoft Word, and Google Docs. Vellum preserves your headers, italics, scene breaks, and chapter structure — so you're not starting over, just finishing up.`,
          checkList: [
            `Scrivener .scriv projects (Mac)`,
            `Microsoft Word .docx files`,
            `Google Docs via direct import`,
            `Chapter headings and scene breaks preserved`,
            `Front matter and backmatter added automatically`,
          ],
        },
      },
      cta: {
        eyebrow: `Ready to publish everywhere?`,
        title: `Your manuscript.`,
        titleEm: `Every format, every store.`,
        sub: `One Pro subscription unlocks ebook export, print-ready PDFs, validated files for every platform, box sets, and every other Pro feature. Upgrade in thirty seconds.`,
        upgrade: `Upgrade to Pro — $18/mo`,
        allFeatures: `See all Pro features`,
      },
      footer: {
        tagline: `Long-form writing, honestly monetised.`,
        ebookExport: `Ebook Export`,
        pro: `Pro`,
        platform: `Platform`,
        support: `Support`,
        links: {
          platforms: `Platforms`,
          print: `Print`,
          workflow: `Workflow`,
          upgrade: `Upgrade`,
          allFeatures: `All features`,
          pricing: `Pricing`,
          freeVsPro: `Compare plans`,
          home: `Home`,
          discover: `Discover`,
          getStarted: `Get started`,
        },
      },
    },
    adminDashboard: {
      title: `Admin Dashboard`,
      loggedInAs: `Logged in as`,
      tabs: {
        overview: `Overview`,
        users: `Users`,
        content: `Content`,
        financial: `Financial`,
        trust: `Trust & Safety`,
      },
      loading: {
        analytics: `Loading analytics…`,
        users: `Loading user data…`,
        content: `Loading content data…`,
        financial: `Loading financial data…`,
        trust: `Loading trust data…`,
        generic: `Loading…`,
        reports: `Loading reports…`,
        appeals: `Loading appeals…`,
        usersTab: `Loading users…`,
        contentTab: `Loading content…`,
      },
      overview: { title: `Platform Overview` },
      users: {
        title: `User Metrics`,
        charts: {
          growth: `User Growth`,
          byRole: `Users by Role`,
        },
      },
      content: {
        title: `Content Metrics`,
        charts: {
          publishActivity: `Publish Activity`,
          statusDistribution: `Status Distribution`,
          topByViews: `Top Journals by Views`,
        },
        tabs: {
          all: `All Content`,
          pending: `Pending Approval`,
        },
        empty: `No content awaiting approval.`,
        noContent: `No content found`,
        filters: {
          allStatuses: `All statuses`,
          scheduled: `Scheduled`,
          archived: `Archived`,
          published: `Published`,
          draft: `Draft`,
        },
        table: {
          title: `Title`,
          author: `Author`,
          views: `Views`,
          likes: `Likes`,
          words: `Words`,
          tags: `Tags`,
          submitted: `Submitted`,
          status: `Status`,
          actions: `Actions`,
          published: `Published`,
        },
        contentRejectReason: `Reason for rejection…`,
        contentSearch: `Search title, content…`,
        contentAuthorUsername: `Author username`,
        contentFilterByStatus: `Filter by status`,
      },
      financial: {
        title: `Financial Metrics`,
        charts: {
          subscriptionActivity: `Subscription Activity`,
          topCreators: `Top Creators by Subscribers`,
        },
      },
      trust: {
        title: `Trust & Safety Metrics`,
        charts: {
          reportActivity: `Report Activity`,
          reportsByType: `Reports by Type`,
        },
      },
      reports: {
        loading: `Loading reports…`,
        noReports: `No reports found`,
        filters: {
          allTypes: `All types`,
        },
        table: {
          reporter: `Reporter`,
          reported: `Reported`,
          type: `Type`,
          reason: `Reason`,
          content: `Content`,
          date: `Date`,
        },
        fields: {
          reporter: `Reporter`,
          reportedUser: `Reported User`,
          typeAndReason: `Type & Reason`,
          content: `Reported Content`,
          comment: `Reported Comment`,
          additionalInfo: `Additional Info`,
          activityLog: `Activity Log`,
          moderatorNote: `Moderator Note`,
        },
        reportsLoadingDetail: `Loading detail…`,
        reportsLoadingHistory: `Loading history…`,
        reportsNoActivity: `No activity recorded yet.`,
        reportsMarkReviewing: `Mark as Reviewing`,
        reportsFilterByType: `Filter by report type`,
      },
      appeals: {
        loading: `Loading appeals…`,
        notePlaceholder: `Optional note to author…`,
        noAppeals: `No appeals found.`,
        table: {
          user: `User`,
          journal: `Journal`,
          appeal: `Appeal message`,
        },
        hidden: `Hidden`,
        contentDeleted: `Content deleted`,
      },
      usersTab: {
        loading: `Loading users…`,
        noUsers: `No users found`,
        filters: {
          allRoles: `All roles`,
          roles: {
            user: `User`,
            moderator: `Moderator`,
            admin: `Admin`,
          },
        },
        table: {
          user: `User`,
          role: `Role`,
          superviewer: `Superviewer`,
          journals: `Journals`,
          subscribers: `Subscribers`,
          joined: `Joined`,
        },
        sections: {
          activeRestrictions: `Active restrictions`,
          accountDetails: `Account details`,
          links: `Links`,
          addRestriction: `Add restriction`,
        },
        restrictions: {
          none: `No active restrictions.`,
          options: {
            commentsDisabled: `Comments disabled`,
            newContentRequiresApproval: `New content requires approval`,
            discoveryHidden: `Hidden from discovery`,
            postingRateLimited: `Posting rate limited`,
            reportsDisabled: `Reports disabled`,
            nsfwPublishingBlocked: `NSFW publishing blocked`,
          },
        },
        searchPlaceholder: `Search username, email, name…`,
        filterByRoleAria: `Filter by role`,
        expandRow: `Expand row`,
        addRestrictionBtn: `+ Add restriction`,
        restrictionTypeAria: `Restriction type`,
        reasonPlaceholder: `Reason (required, visible in audit log)…`,
        expiryPlaceholder: `Expiry date (optional)`,
        closeBtn: `Close`,
        applyRestriction: `Apply restriction`,
        creatorTier: `Creator tier`,
        reportsFiled: `Reports filed`,
        badge: `Badge`,
        badged: `Badged`,
      },
      analyticsTab: {
        analyticsTopFlagged: `Top flagged journals by community AI reports`,
        analyticsAutoLabel: `Auto label`,
        analyticsManualLabel: `Manual label`,
      },
    },
  },
} as const;

// Full translation shape — inferred from the English source of truth
export type Translations = typeof en;

// Helper: allows other locales to implement only the keys they override;
// i18next falls back to English (fallbackLng: "en") for missing keys at runtime.
export type PartialTranslations = DeepPartial<Translations>;

type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : string;
};

export default en;
